import { GraphDto } from "@custom-types/graphs"
import { GraphContainer } from "@features/graph/GraphContainer"
import { GraphEditMode, GraphNodeSelection } from "@features/graph/graphViewModel"
import GraphMetadataCard from "@features/scenarioDesign/GraphMetadataCard"
import ParametersUploadDialog from "@features/upload/ParametersUploadDialog"
import { Grid, Typography } from "@mui/material"
import { Viewport } from "reactflow"
import { GraphDetailsContainer } from "@features/graph/details/GraphDetailsContainer"
import { useCallback, useEffect, useRef, useState } from "react"

export type CreateScenarioModeProps = {
  graph: GraphDto | undefined
  editMode: GraphEditMode
  viewPort?: Viewport
  onViewPortChange?: (viewPort: Viewport) => void
  isImportParametersDialogOpen: boolean
  onCloseImportParametersDialog: () => void
}

const CreateScenarioMode = ({
  graph,
  editMode,
  viewPort,
  onViewPortChange,
  isImportParametersDialogOpen,
  onCloseImportParametersDialog,
}: CreateScenarioModeProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [selectedNodes, setSelectedNodes] = useState<GraphNodeSelection | undefined>(undefined)
  const gridRef = useRef<HTMLDivElement | null>(null)

  const onNodeSelection = useCallback((selection: GraphNodeSelection) => {
    if (!selection.isExpandingIconClicked) {
      setIsDrawerOpen(selection.nodes.length > 0)
    }

    setSelectedNodes(selection.nodes.length > 0 ? selection : undefined)
  }, [])

  useEffect(() => {
    setIsDrawerOpen(false)
  }, [graph])

  if (!graph) {
    return (
      <Grid container item sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}>
        <Grid item>
          <Typography variant={"h6"}>Please select a graph family from the sidebar</Typography>
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <Grid item xs ref={gridRef} sx={{ position: "relative", overflow: "initial !important" }}>
        <GraphContainer
          graph={graph}
          viewPort={viewPort}
          onViewPortChange={onViewPortChange}
          isUserGraph={false}
          showModified={true}
          showUserGraphIndicator={false}
          showResetGraph={true}
          showExportGraph={true}
          selectionChangeCallback={onNodeSelection}
          editMode={editMode}
          selectParentChild={true}
        />
        <GraphDetailsContainer
          editMode={editMode}
          graphKey={graph?.key}
          selectedNodes={selectedNodes}
          isOpen={isDrawerOpen}
          closeCallback={() => setIsDrawerOpen(false)}
          container={gridRef.current}
          isMultiselectVisible={false}
          localStoragePrefix="scenario-design-page"
          isScenarioDesignPage
        />
      </Grid>
      <Grid item sx={{ mt: "30px" }}>
        <GraphMetadataCard graph={graph} />
      </Grid>
      {isImportParametersDialogOpen && (
        <ParametersUploadDialog onClose={onCloseImportParametersDialog} graphKey={graph.key} />
      )}
    </>
  )
}

export default CreateScenarioMode
